import React, { useState, useEffect, useContext } from "react";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import { X as LucideX } from "lucide-react";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ChatiaDialogActions from "../ChatiaDialogActions";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Grid, ListItemText, MenuItem, Select } from "@material-ui/core";
import { toast } from "react-toastify";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import ShowTicketOpen from "../ShowTicketOpenModal";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: "21px 21px 12px 12px !important",
    maxHeight: "85vh !important",
    display: "flex !important",
    flexDirection: "column !important",
    overflow: "hidden !important",
    background: theme.mode === "dark" ? "#121212" : "#ffffff",
    border: "none",
    "& .MuiDialogContent-root": {
      flex: 1,
      overflowY: "auto !important",
      overflowX: "hidden",
      background: theme.mode === "dark" ? "#121212" : "#ffffff",
      ...theme.scrollbarStyles,
    },
    "& .MuiDialogActions-root": {
      flexShrink: 0,
      background: theme.mode === "dark" ? "#121212" : "#ffffff",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
    },
    "& .MuiButton-root": {
      borderRadius: 12,
    },
    "& .MuiSelect-icon": {
      right: 8,
    },
  },
  selectMenu: {
    borderRadius: "12px !important",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08) !important",
    "& .MuiList-root": {
      padding: 4,
      ...theme.scrollbarStyles,
    },
    "& .MuiMenuItem-root": {
      borderRadius: 7,
      fontSize: 14,
      "&:hover": {
        backgroundColor: theme.mode === "light" ? "#f5f0eb !important" : "#2a3942 !important",
      },
      "&.Mui-selected": {
        backgroundColor: theme.mode === "light" ? "#ebe5df !important" : "#354a56 !important",
      },
      "&.Mui-selected:hover": {
        backgroundColor: theme.mode === "light" ? "#e0d8d0 !important" : "#3a5060 !important",
      },
    },
  },
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText || "#ffffff",
    borderBottom: "none",
    flexShrink: 0,
  },
  dialogHeaderTitle: {
    fontWeight: 600,
    fontSize: 15,
    color: "inherit",
  },
}));

const filter = createFilterOptions({
  trim: true,
});

const NewTicketModal = ({ modalOpen, onClose, initialContact }) => {
  const classes = useStyles();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [whatsapps, setWhatsapps] = useState([]);
  const [newContact, setNewContact] = useState({});
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const { user } = useContext(AuthContext);

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  useEffect(() => {
    if (initialContact?.id !== undefined) {
      setOptions([initialContact]);
      setSelectedContact(initialContact);
    }
  }, [initialContact]);

  useEffect(() => {
    if (user.queues.length === 1) {
      setSelectedQueue(user.queues[0].id)
    }
  }, [])

  useEffect(() => {
    if (!modalOpen) return;
    const fetchWhatsapps = async () => {
      try {
        const { data } = await api.get("/whatsapp", { params: { session: 0 } });
        const connected = data.filter(w => w.status === "CONNECTED");
        setWhatsapps(connected);
        if (user.whatsappId) {
          setSelectedWhatsapp(user.whatsappId);
        } else if (connected.length === 1) {
          setSelectedWhatsapp(connected[0].id);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchWhatsapps();
  }, [modalOpen])

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
      default:
        return "error";
    }
  };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
    setSelectedContact(null);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setLoading(false);
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
  };

  const handleSaveTicket = async contactId => {
    if (!contactId) return;
    if (selectedQueue === "") {
      toast.error(i18n.t("newTicketModal.validation.queueRequired"));
      return;
    }
    if (selectedWhatsapp === "") {
      toast.error(i18n.t("newTicketModal.validation.connectionRequired") || "Seleccione una conexión");
      return;
    }

    setLoading(true);
    try {
      const queueId = selectedQueue !== "" ? selectedQueue : null;
      const whatsappId = selectedWhatsapp || user.whatsappId || null;
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        queueId,
        whatsappId,
        userId: user.id,
        status: "open",
      });

      onClose(ticket);
    } catch (err) {

      const ticket = JSON.parse(err.response.data.error);

      if (ticket.userId !== user?.id) {
        setOpenAlert(true);
        setUserTicketOpen(ticket?.user?.name);
        setQueueTicketOpen(ticket?.queue?.name);
      } else {
        setOpenAlert(false);
        setUserTicketOpen("");
        setQueueTicketOpen("");
        setLoading(false);
        onClose(ticket);
      }
    }
    setLoading(false);
  };

  const handleSelectOption = (e, newValue) => {
    if (newValue?.number) {
      setSelectedContact(newValue);
    } else if (newValue?.name) {
      setNewContact({ name: newValue.name });
      setContactModalOpen(true);
    }
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleAddNewContactTicket = contact => {
    setSelectedContact(contact);
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
      });
    }
    return filtered;
  };

  const renderOption = option => {
    if (option.number) {
      return <>
        {IconChannel(option.channel)}
        <Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
          {option.name} - {option.number}
        </Typography>
      </>
    } else {
      return `${i18n.t("newTicketModal.add")} ${option.name}`;
    }
  };

  const renderOptionLabel = option => {
    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${option.name}`;
    }
  };

  const renderContactAutocomplete = () => {
    if (initialContact === undefined || initialContact.id === undefined) {
      return (
        <Grid xs={12} item>
          <Autocomplete
            fullWidth
            options={options}
            loading={loading}
            clearOnBlur
            autoHighlight
            freeSolo
            clearOnEscape
            getOptionLabel={renderOptionLabel}
            renderOption={renderOption}
            filterOptions={createAddContactOption}
            onChange={(e, newValue) => handleSelectOption(e, newValue)}
            renderInput={params => (
              <TextField
                {...params}
                label={i18n.t("newTicketModal.fieldLabel")}
                variant="outlined"
                autoFocus
                onChange={e => setSearchParam(e.target.value)}
                onKeyPress={e => {
                  if (loading || !selectedContact) return;
                  else if (e.key === "Enter") {
                    handleSaveTicket(selectedContact.id);
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
      )
    }
    return null;
  }

  return (
    <>

      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.dialogHeader}>
          <Typography className={classes.dialogHeaderTitle}>
            {i18n.t("newTicketModal.title")}
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              color: "#ffffff",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.18)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
            }}
          >
            <LucideX size={18} strokeWidth={2.5} />
          </IconButton>
        </div>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* CONTATO */}
            {renderContactAutocomplete()}
            {/* FILA */}
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant="outlined"
                value={selectedQueue}
                onChange={(e) => {
                  setSelectedQueue(e.target.value)
                }}
                MenuProps={{
                  classes: { paper: classes.selectMenu },
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedQueue === "") {
                    return i18n.t("newTicketModal.form.queuePlaceholder")
                  }
                  const queue = user.queues.find(q => q.id === selectedQueue)
                  return queue.name
                }}
              >
                {user.queues?.length > 0 &&
                  user.queues.map((queue, key) => (
                    <MenuItem dense key={key} value={queue.id}>
                      <ListItemText primary={queue.name} />
                    </MenuItem>
                  ))
                }
              </Select>
            </Grid>
            {/* CONEXIÓN */}
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant="outlined"
                value={selectedWhatsapp}
                onChange={(e) => {
                  setSelectedWhatsapp(e.target.value)
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedWhatsapp === "") {
                    return i18n.t("newTicketModal.form.connectionPlaceholder") || "Seleccione una conexión"
                  }
                  const wp = whatsapps.find(w => w.id === selectedWhatsapp)
                  return wp ? wp.name : ""
                }}
              >
                {whatsapps.map((wp) => (
                  <MenuItem dense key={wp.id} value={wp.id}>
                    <ListItemText primary={wp.name} />
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </DialogContent>
        <ChatiaDialogActions
          type="button"
          disabled={!selectedContact}
          onClick={() => handleSaveTicket(selectedContact.id)}
          loading={loading}
          saveText={i18n.t("newTicketModal.buttons.ok")}
        />
        {contactModalOpen && (
          <ContactModal
            open={contactModalOpen}
            initialValues={newContact}
            onClose={handleCloseContactModal}
            onSave={handleAddNewContactTicket}
          ></ContactModal>
        )}
        {openAlert && (
          <ShowTicketOpen
            isOpen={openAlert}
            handleClose={handleCloseAlert}
            user={userTicketOpen}
            queue={queueTicketOpen}
          />
        )}
      </Dialog >
    </>
  );
};
export default NewTicketModal;